import { useCallback } from 'react';
import { useTracking } from 'react-tracking';
import { useFlag } from '@simplywallst/unleash';
import { FairValueHistogram } from '../../FairValueHistogram';
import type { Narrative } from '../hooks/useCompanyNarratives';
import { useCompanyNarratives } from '../hooks/useCompanyNarratives';
import { CompanyNarrativesList } from './CompanyNarrativesList';
import { useMemo } from 'react';
import { getCompanyUrlSegments } from '@/pages/Narrative/utils/getNarrativeUrl';
import { CompanyNarrativesHeader } from './CompanyNarrativesHeader';
import { useMount } from '@simplywallst/ui-core';
import { CompanyNarrativeButtonExperiment } from './CompanyNarrativeButtonExperiment';
import {
  NarrativeCard,
  NarrativeCardHorizontal,
} from '@/components/NarrativeCard';
import { useUser } from '@/hooks/useUser';
import { useUserAccess } from '@/hooks/useUserAccess';

type Props = {
  canonicalUrl: string;
};

export const CompanyNarrativesMain = ({ canonicalUrl }: Props) => {
  const { trackEvent } = useTracking();
  const { data: user } = useUser();
  const { data: userAccess } = useUserAccess();
  const { data } = useCompanyNarratives({ canonicalUrl });
  const { variant } = useFlag('mono-sponsored-company-narratives');

  const { company } = data;
  const isFreeUser = userAccess.planType === 'free';
  const enableSponsoredNarratives = isFreeUser || !user.loggedIn;
  const enableSponsoredCompanyNarrativesExperiment =
    variant?.name === 'variant';

  const communityNarrativeCount = data?.narratives.edges.length ?? 0;
  const primaryCount = company.valuationHistogram?.primaryCount ?? 0;
  const shouldShowHistogram = primaryCount > 5;
  const sponsoredCompanyNarratives = company.sponsoredNarratives ?? [];

  const handleOnClick = useCallback(
    (narrative: (typeof narratives)[number]) => {
      const { id, company } = narrative;
      return trackEvent({
        action: 'click',
        modifier: 'view',
        narrativeId: id,
        companyId: company?.id,
        uniqueSymbol: company?.tickerSymbol,
      });
    },
    [trackEvent]
  );

  const narratives = useMemo(() => {
    const { narratives: narrativeConnections, company } = data;
    return [
      company.myValuation?.narrative,
      company.myNarrative,
      ...sponsoredCompanyNarratives,
      ...narrativeConnections.edges.map((edge) => edge.node),
    ]
      .reduce((result, narrative) => {
        if (!narrative || result.some((item) => item.id === narrative.id))
          return result;

        return [...result, narrative];
      }, [] as Narrative[])
      .reduce((acc: Narrative[], narrative: Narrative) => {
        const shouldRemove =
          enableSponsoredCompanyNarrativesExperiment &&
          enableSponsoredNarratives &&
          narrative.isSponsored;

        if (!shouldRemove) {
          acc.push(narrative);
        }

        return acc;
      }, []);
  }, [
    data,
    enableSponsoredCompanyNarrativesExperiment,
    sponsoredCompanyNarratives,
  ]);

  useMount(() => {
    trackEvent({
      subject: 'narratives',
      action: 'loaded',
      narratives: narratives.map((narrative) => ({ id: narrative.id })),
    });
  });

  const { country, industry, exchangeTicker, companyName } =
    getCompanyUrlSegments(canonicalUrl);

  return (
    <>
      <CompanyNarrativesHeader
        companyId={company.id}
        tickerSymbol={company.tickerSymbol}
      />
      <section className="grid gap-x2">
        <p className="m-0 text-sm text-soft">
          {shouldShowHistogram
            ? `See what ${primaryCount} others think this stock is worth. Follow their fair value or set your own to get alerts.`
            : `See what others think this stock is worth. Follow their fair value or set your own to get alerts.`}
        </p>
        {shouldShowHistogram && (
          <FairValueHistogram company={company} height={270} />
        )}
        <CompanyNarrativesList
          canonicalUrl={canonicalUrl}
          narratives={narratives}
          hasUserNarrative={!!company.myNarrative}
        />
        <div className="grid">
          <CompanyNarrativeButtonExperiment
            canonicalUrl={canonicalUrl}
            country={country}
            industry={industry}
            companyName={companyName}
            companyNarrativeName={company.name}
            exchangeTicker={exchangeTicker}
            communityNarrativeCount={communityNarrativeCount}
          />
        </div>
        {enableSponsoredNarratives &&
          enableSponsoredCompanyNarrativesExperiment &&
          sponsoredCompanyNarratives?.length !== 0 && (
            <>
              <div className="hidden lg:block">
                <NarrativeCardHorizontal
                  showCompanyIcon
                  narrativeId={sponsoredCompanyNarratives[0]?.id}
                  onClick={() => handleOnClick(sponsoredCompanyNarratives[0])}
                />
              </div>
              <div className="block lg:hidden">
                <NarrativeCard
                  showCompanyIcon
                  narrativeId={sponsoredCompanyNarratives[0]?.id}
                  onClick={() => handleOnClick(sponsoredCompanyNarratives[0])}
                />
              </div>
            </>
          )}
      </section>
    </>
  );
};
